import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Radar, Loader2, Trash2, LayoutGrid, List, Send, Check, Save, Star, EyeOff, Eye, Sparkles, ShieldAlert, X } from "lucide-react";
import { getBackendUrl, authHeaders } from "../lib/http";
import { callExtension } from "../lib/extension";
import { type DiscoveredListing, type AiEval, fmt, growth, similarGroups, KeywordSummary, TopWords } from "../components/discovery/DiscoveryInsights";
type DiscoveryResponse = { items: DiscoveredListing[]; analyzed?: Record<string, string>; keywords: Array<{ keyword: string; count: number; lastScannedAt: string }> };

type SortKey = "ai" | "potential" | "views24" | "inCarts" | "favPerDay" | "viewsPerDay" | "viewsGain" | "favGain" | "newest";
const SORTS: Array<[SortKey, string]> = [
  ["potential", "Potansiyel"],
  ["ai", "AI puanı"],
  ["views24", "24 saat görüntülenme"],
  ["inCarts", "Sepet"],
  ["favPerDay", "Günlük favori"],
  ["viewsPerDay", "Günlük görüntülenme"],
  ["viewsGain", "Görüntülenme artışı"],
  ["favGain", "Favori artışı"],
  ["newest", "En yeni liste"],
];

const INPUT = "rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300";
const NOT_DEEP = "Ön filtreye uymadı; sayfa açılmadı";

// Onceki taramadan bu yana gunluk artis metni; artis > 0 ise yesil
function GrowthText({ it, stacked }: { it: DiscoveredListing; stacked?: boolean }) {
  const g = growth(it);
  if (!g) return null;
  const part = (v: number | null, label: string) => v == null ? null : (
    <span className={`${stacked ? "block" : ""} ${v > 0 ? "text-emerald-700" : ""}`}>{v > 0 ? "+" : ""}{fmt(v, 1)} {label}/gün</span>
  );
  return <>{part(g.views, "görüntülenme")}{!stacked && g.views != null && g.fav != null && " · "}{part(g.fav, "favori")}</>;
}
// Yila ayrilma eki: 2023'ten, 2020'den, 2019'dan (son okunan kelimeye gore)
const sinceYear = (y: number) =>
  `${y}'${y % 10 ? ["", "den", "den", "ten", "ten", "ten", "dan", "den", "den", "dan"][y % 10] : (["den", "dan", "den", "dan", "tan"][(y / 10) % 10] ?? "dan")} beri`;
const shopInfo = (it: DiscoveredListing) => [
  it.shopSales != null ? `${fmt(it.shopSales)} satış` : "",
  it.shopCreatedAt ? sinceYear(new Date(it.shopCreatedAt).getFullYear()) : "",
].filter(Boolean).join(" · ");

// Arama kartindaki kucuk gorseli (il_340x270) Etsy'nin buyuk boyutuna cevir
const bigImage = (u: string) => u.replace(/il_\d+x(?:\d+|N)\./, "il_570xN.");

const VIEW_KEY = "erc_discovery_view";
// Filtreler: son kullanilan tarayicida (FILTERS_KEY), adlandirilmis kayitlar hesapta (/api/settings PRESETS_SETTING)
type Filters = { keyword: string; sort: SortKey; maxAge: string; minPotential: string; onlyDeep: boolean; hideAds: boolean; hideAnalyzed: boolean; exclude: string; onlyWatched: boolean; showHidden: boolean; hideIp?: boolean };
const DEFAULT_FILTERS: Filters = { keyword: "", sort: "potential", maxAge: "", minPotential: "", onlyDeep: false, hideAds: false, hideAnalyzed: false, exclude: "", onlyWatched: false, showHidden: false, hideIp: false };
const FILTERS_KEY = "erc_discovery_filters";
const PRESETS_SETTING = "discovery_filter_presets";
type Preset = { name: string; filters: Filters };
const loadFilters = (): Filters => {
  try { return { ...DEFAULT_FILTERS, ...(JSON.parse(localStorage.getItem(FILTERS_KEY) ?? "{}") as Partial<Filters>) }; } catch { return DEFAULT_FILTERS; }
};
const loadView = (): "grid" | "table" => { try { return localStorage.getItem(VIEW_KEY) === "table" ? "table" : "grid"; } catch { return "grid"; } };

// Telif riski tasiyabilecek kelimeler (marka, lisansli karakter, unlu, takim).
// Kullanici duzenler; hesapta saklanir (IP_SETTING). Tam kelime eslesir.
const IP_SETTING = "discovery_ip_terms";
const DEFAULT_IP_TERMS = [
  "disney", "pixar", "marvel", "avengers", "star wars", "mickey", "minnie", "snoopy", "charlie brown", "peanuts gang",
  "harry potter", "hogwarts", "pokemon", "pikachu", "sanrio", "hello kitty", "kuromi", "my melody", "barbie", "grinch",
  "jack skellington", "nightmare before christmas", "lilo", "winnie the pooh", "elsa", "bluey", "sesame street",
  "looney tunes", "scooby", "nike", "adidas", "nfl", "nba", "mlb", "nhl", "taylor swift", "swiftie", "eras tour",
  "beatles", "stranger things", "hocus pocus", "beetlejuice", "michael myers", "freddy krueger", "jason voorhees",
  "chucky", "ghostface", "care bears", "garfield", "spongebob", "super mario", "zelda", "minecraft", "labubu",
  "sonic", "hello neighbor", "paw patrol", "coca cola", "starbucks", "harley davidson", "jeep", "yellowstone",
];
const escRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const ipMatcher = (terms: string[]) => {
  const t = terms.map((x) => x.trim().toLowerCase()).filter(Boolean);
  if (!t.length) return () => null as string | null;
  const re = new RegExp(`\\b(${t.map(escRe).join("|")})\\b`, "i");
  return (title: string) => title.match(re)?.[1]?.toLowerCase() ?? null;
};

// Onemli metrik renkleri: [iyi esigi, orta esigi]; ters=true ise kucuk deger iyidir (yas)
function toneCls(v: number | null | undefined, good: number, mid: number, inverse = false) {
  if (v == null) return "text-gray-500";
  const isGood = inverse ? v <= good : v >= good;
  const isMid = inverse ? v <= mid : v >= mid;
  return isGood ? "font-semibold text-emerald-700" : isMid ? "font-medium text-amber-700" : "text-gray-500";
}
const AGE = [30, 90] as const, VPD = [20, 5] as const, FPD = [1, 0.3] as const, CARTS = [10, 3] as const, V24 = [50, 15] as const, SALES = [3, 1] as const;

const VERDICT: Record<AiEval["verdict"], [string, string]> = {
  go: ["Yap", "bg-emerald-50 text-emerald-700"],
  caution: ["Dikkat", "bg-amber-50 text-amber-700"],
  skip: ["Geç", "bg-red-50 text-red-600"],
};
const CP_LEVEL: Record<AiEval["copyright"]["level"], [string, string]> = {
  low: ["Telif düşük", "bg-emerald-50 text-emerald-700"],
  medium: ["Telif orta", "bg-amber-50 text-amber-700"],
  high: ["Telif yüksek", "bg-red-50 text-red-600"],
};

// Rozet (pill) rengi: iyi yesil, orta sari, zayif gri
function pillCls(v: number | null | undefined, good: number, mid: number) {
  if (v == null) return "bg-gray-100 text-gray-600";
  return v >= good ? "bg-emerald-50 font-semibold text-emerald-700" : v >= mid ? "bg-amber-50 font-medium text-amber-700" : "bg-gray-100 text-gray-600";
}

function potentialCls(p: number) {
  if (p >= 70) return "bg-emerald-50 text-emerald-700";
  if (p >= 45) return "bg-amber-50 text-amber-700";
  return "bg-gray-100 text-gray-600";
}

export default function DiscoveryPage() {
  const qc = useQueryClient();
  const [initial] = useState(loadFilters);
  const [keyword, setKeyword] = useState(initial.keyword);
  const [sort, setSort] = useState<SortKey>(initial.sort);
  const [maxAge, setMaxAge] = useState(initial.maxAge);
  const [minPotential, setMinPotential] = useState(initial.minPotential);
  const [onlyDeep, setOnlyDeep] = useState(initial.onlyDeep);
  const [hideAds, setHideAds] = useState(initial.hideAds);
  const [hideAnalyzed, setHideAnalyzed] = useState(initial.hideAnalyzed);
  // Basliginda bu kelimelerden biri gecen urunler elenir (virgulle ayrilmis, buyuk/kucuk harf farketmez)
  const [exclude, setExclude] = useState(initial.exclude);
  const changeExclude = setExclude;
  const [onlyWatched, setOnlyWatched] = useState(initial.onlyWatched);
  const [showHidden, setShowHidden] = useState(initial.showHidden);
  // Telifli kelime havuzuna takilanlari gizle (AI "telif yuksek" dediklerini de)
  const [hideIp, setHideIp] = useState(Boolean(initial.hideIp));
  const current: Filters = { keyword, sort, maxAge, minPotential, onlyDeep, hideAds, hideAnalyzed, exclude, onlyWatched, showHidden, hideIp };
  const applyFilters = (f: Filters) => {
    setKeyword(f.keyword); setSort(f.sort); setMaxAge(f.maxAge); setMinPotential(f.minPotential);
    setOnlyDeep(f.onlyDeep); setHideAds(f.hideAds); setHideAnalyzed(f.hideAnalyzed); setExclude(f.exclude);
    setOnlyWatched(f.onlyWatched); setShowHidden(f.showHidden); setHideIp(Boolean(f.hideIp));
  };
  // Son kullanilan filtreler bu tarayicida hatirlanir
  useEffect(() => { try { localStorage.setItem(FILTERS_KEY, JSON.stringify(current)); } catch { /* */ } },
    [keyword, sort, maxAge, minPotential, onlyDeep, hideAds, hideAnalyzed, exclude, onlyWatched, showHidden, hideIp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Adlandirilmis filtre kayitlari (hesapta; baska bilgisayarda da gorunur)
  const presetsQ = useQuery({
    queryKey: ["settings", PRESETS_SETTING],
    queryFn: async (): Promise<Preset[]> => {
      const r = await fetch(`${getBackendUrl()}/api/settings`, { headers: authHeaders() });
      const raw = (await r.json().catch(() => ({})))?.data?.[PRESETS_SETTING];
      try { return raw ? (JSON.parse(raw) as Preset[]) : []; } catch { return []; }
    },
  });
  const presets = presetsQ.data ?? [];
  const [presetName, setPresetName] = useState("");
  const [activePreset, setActivePreset] = useState("");
  const savePresets = useMutation({
    mutationFn: async (next: Preset[]) => {
      const r = await fetch(`${getBackendUrl()}/api/settings`, {
        method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ [PRESETS_SETTING]: JSON.stringify(next) }),
      });
      if (!r.ok) throw new Error(`Filtre kaydedilemedi (${r.status})`);
      return next;
    },
    onSuccess: (next) => qc.setQueryData(["settings", PRESETS_SETTING], next),
  });
  const onSavePreset = () => {
    const name = presetName.trim() || activePreset;
    if (!name) return;
    savePresets.mutate([...presets.filter((x) => x.name !== name), { name, filters: current }].sort((a, b) => a.name.localeCompare(b.name, "tr")));
    setActivePreset(name);
    setPresetName("");
  };
  const onPickPreset = (name: string) => {
    setActivePreset(name);
    const p = presets.find((x) => x.name === name);
    if (p) applyFilters({ ...DEFAULT_FILTERS, ...p.filters });
  };
  const onDeletePreset = () => {
    if (!activePreset || !confirm(`"${activePreset}" filtresi silinsin mi?`)) return;
    savePresets.mutate(presets.filter((x) => x.name !== activePreset));
    setActivePreset("");
  };
  // Telifli kelime havuzu (hesapta)
  const ipQ = useQuery({
    queryKey: ["settings", IP_SETTING],
    queryFn: async (): Promise<string[]> => {
      const r = await fetch(`${getBackendUrl()}/api/settings`, { headers: authHeaders() });
      const raw = (await r.json().catch(() => ({})))?.data?.[IP_SETTING];
      try { return raw ? (JSON.parse(raw) as string[]) : DEFAULT_IP_TERMS; } catch { return DEFAULT_IP_TERMS; }
    },
  });
  const ipTerms = ipQ.data ?? DEFAULT_IP_TERMS;
  const ipHit = useMemo(() => ipMatcher(ipTerms), [ipTerms]);
  const [ipEdit, setIpEdit] = useState<string | null>(null);
  const saveIp = useMutation({
    mutationFn: async (terms: string[]) => {
      const r = await fetch(`${getBackendUrl()}/api/settings`, {
        method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ [IP_SETTING]: JSON.stringify(terms) }),
      });
      if (!r.ok) throw new Error(`Kelime havuzu kaydedilemedi (${r.status})`);
      return terms;
    },
    onSuccess: (terms) => { qc.setQueryData(["settings", IP_SETTING], terms); setIpEdit(null); },
  });

  // AI degerlendirmesi: secililer, yoksa listedeki ilk 20 degerlendirilmemis urun
  const [aiMsg, setAiMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const aiEval = useMutation({
    mutationFn: async (ids: string[]) => {
      const r = await fetch(`${getBackendUrl()}/api/discovery/ai-eval`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ ids }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error ?? `AI değerlendirmesi alınamadı (${r.status})`);
      return j as { evals: Record<string, AiEval>; failed: number };
    },
    onSuccess: ({ evals, failed }) => {
      const prev = qc.getQueryData<DiscoveryResponse>(["discovery"]);
      if (prev) qc.setQueryData<DiscoveryResponse>(["discovery"], { ...prev, items: prev.items.map((x) => (evals[x.id] ? { ...x, aiEval: evals[x.id] } : x)) });
      const n = Object.keys(evals).length;
      const high = Object.values(evals).filter((e) => e.copyright.level === "high").length;
      setAiMsg({ ok: true, text: `${n} ürün değerlendirildi${high ? ` · ${high} üründe telif riski yüksek` : ""}${failed ? ` · ${failed} ürün değerlendirilemedi` : ""}. Sıralamayı "AI puanı" yaparak en iyileri üste alabilirsin.` });
      setSelected(new Set());
    },
    onError: (e) => setAiMsg({ ok: false, text: (e as Error).message }),
  });
  const [view, setView] = useState<"grid" | "table">(loadView);
  const changeView = (v: "grid" | "table") => { setView(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* */ } };
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sendMsg, setSendMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [sending, setSending] = useState(false);
  const toggle = (id: string) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const lastPick = useRef<number | null>(null);

  // Tum kayitlar tek seferde gelir (en fazla 2000); anahtar kelime filtresi istemcide
  const { data, isLoading, error } = useQuery({
    queryKey: ["discovery"],
    refetchInterval: 20000, // tarama surerken yeni sayfalar kendiliginden gelsin
    queryFn: async (): Promise<DiscoveryResponse> => {
      const r = await fetch(`${getBackendUrl()}/api/discovery`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`Keşif sonuçları alınamadı (${r.status})`);
      return r.json();
    },
  });

  const clear = useMutation({
    mutationFn: async () => {
      const q = keyword ? `?keyword=${encodeURIComponent(keyword)}` : "";
      const r = await fetch(`${getBackendUrl()}/api/discovery${q}`, { method: "DELETE", headers: authHeaders() });
      if (!r.ok) throw new Error(`Silinemedi (${r.status})`);
      return r.json() as Promise<{ deleted: number }>;
    },
    onSuccess: () => { setKeyword(""); qc.invalidateQueries({ queryKey: ["discovery"] }); },
  });

  // Takip / gizle: onbellek hemen guncellenir, hata olursa geri alinir
  const patch = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: { watched?: boolean; hidden?: boolean } }) => {
      const r = await fetch(`${getBackendUrl()}/api/discovery/${id}`, {
        method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`Güncellenemedi (${r.status})`);
      return r.json() as Promise<{ item: DiscoveredListing }>;
    },
    onMutate: async ({ id, body }) => {
      await qc.cancelQueries({ queryKey: ["discovery"] });
      const prev = qc.getQueryData<DiscoveryResponse>(["discovery"]);
      if (prev) qc.setQueryData<DiscoveryResponse>(["discovery"], { ...prev, items: prev.items.map((x) => (x.id === id ? { ...x, ...body } : x)) });
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(["discovery"], ctx.prev); },
  });
  const setFlag = (it: DiscoveredListing, body: { watched?: boolean; hidden?: boolean }) => {
    if (body.hidden) setSelected((prev) => { const n = new Set(prev); n.delete(it.id); return n; });
    patch.mutate({ id: it.id, body });
  };

  const filtered = useMemo(() => {
    const age = maxAge === "" ? null : Number(maxAge);
    const minP = minPotential === "" ? null : Number(minPotential);
    const bad = exclude.split(",").map((w) => w.trim().toLowerCase()).filter(Boolean);
    const list = (data?.items ?? []).filter((it) =>
      (showHidden || !it.hidden) &&
      (!hideIp || (!ipHit(it.title) && it.aiEval?.copyright.level !== "high")) &&
      (!onlyWatched || it.watched) &&
      (!bad.length || !bad.some((w) => it.title.toLowerCase().includes(w))) &&
      (!keyword || it.keyword === keyword) &&
      (!onlyDeep || it.deep) &&
      (!hideAds || !it.isAd) &&
      (!hideAnalyzed || !data?.analyzed?.[it.listingId]) &&
      (age == null || (it.ageDays != null && it.ageDays <= age)) &&
      (minP == null || (it.potential != null && it.potential >= minP)),
    );
    // null degerler her zaman sona
    const val = (it: DiscoveredListing) =>
      sort === "newest" ? (it.ageDays == null ? null : -it.ageDays)
        : sort === "viewsGain" ? growth(it)?.views ?? null
        : sort === "favGain" ? growth(it)?.fav ?? null
        : sort === "ai" ? it.aiEval?.score ?? null
        : it[sort];
    return list.sort((a, b) => (val(b) ?? -Infinity) - (val(a) ?? -Infinity));
  }, [data, keyword, sort, maxAge, minPotential, onlyDeep, hideAds, hideAnalyzed, exclude, onlyWatched, showHidden, hideIp, ipHit]);
  // Benzer baslikli ilan gruplari (filtrelenmis listeye gore); bir gruba girilince yalniz o grup gosterilir
  const groups = useMemo(() => similarGroups(filtered), [filtered]);
  const [group, setGroup] = useState<string[] | null>(null);
  const rows = useMemo(() => (group ? filtered.filter((r) => group.includes(r.id)) : filtered), [filtered, group]);
  const analyzedId = (it: DiscoveredListing) => data?.analyzed?.[it.listingId];
  // Tikla: sec/kaldir. Shift+tikla: son tiklanandan buna kadar analiz edilmemislerin hepsini sec
  const pick = (i: number, shift: boolean) => {
    const last = lastPick.current;
    lastPick.current = i;
    if (shift && last != null && last !== i) {
      const [a, b] = last < i ? [last, i] : [i, last];
      setSelected((prev) => { const n = new Set(prev); rows.slice(a, b + 1).forEach((r) => { if (!analyzedId(r)) n.add(r.id); }); return n; });
    } else toggle(rows[i].id);
  };

  // Secilenler eklentinin "+ Analiz" akisiyla analiz kuyruguna: her liste arka plan sekmesinde acilir, kazinir, sekme kapanir
  // Gorev olarak (VDS'teki eklenti alir; /tasks)
  const sendAsTask = async (urls: string[], fallback: boolean) => {
    const r = await fetch(`${getBackendUrl()}/api/tasks`, {
      method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ type: "analyze", payload: { urls } }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setSendMsg({ ok: false, text: j.error ?? `Görev oluşturulamadı (${r.status}).` }); return; }
    setSendMsg({ ok: true, text: `${fallback ? "Bu tarayıcıda eklenti bulunamadı; " : ""}${urls.length} ürün analiz görevi olarak verildi. Görev alan eklenti (ör. VDS) bir dakika içinde alıp analiz kuyruğuna ekler; durum Eklenti Görevleri sayfasında.` });
    setSelected(new Set());
  };

  const sendToAnalysis = async (asTask = false) => {
    const urls = (data?.items ?? []).filter((it) => selected.has(it.id) && !analyzedId(it)).map((it) => it.url);
    if (!urls.length) return;
    setSending(true);
    setSendMsg(null);
    if (asTask) { await sendAsTask(urls, false); setSending(false); return; }
    const r = await callExtension<{ success?: boolean; count?: number; skipped?: number }>({ type: "ANALYZE_URLS", urls });
    if (!r?.success) { await sendAsTask(urls, true); setSending(false); return; }
    setSending(false);
    setSendMsg({ ok: true, text: `${r.count ?? urls.length} ürün analiz kuyruğuna gönderiliyor${r.skipped ? ` (${r.skipped} ürün zaten analiz edilmiş ya da kuyrukta olduğu için atlandı)` : ""}. Ürünler 2,5 saniye arayla arka planda açılıp kapanacak; sonuçlar Ürün Analizleri sayfasında.` });
    setSelected(new Set());
  };

  const onClear = () => {
    const msg = keyword ? `"${keyword}" için kayıtlı tüm sonuçlar silinsin mi?` : "Tüm keşif sonuçları silinsin mi?";
    if (confirm(msg)) clear.mutate();
  };

  const keywords = data?.keywords ?? [];

  // Takip yildizi + gizle/geri al (kart ve tablo satiri)
  const actions = (it: DiscoveredListing) => (
    <>
      <button type="button" onClick={() => setFlag(it, { watched: !it.watched })}
        aria-label={it.watched ? "Takibi bırak" : "Takip et"} title={it.watched ? "Takibi bırak" : "Takip et"} aria-pressed={it.watched}
        className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-amber-500 focus:outline-none focus:ring-2 focus:ring-indigo-200">
        <Star className={`h-4 w-4 ${it.watched ? "fill-amber-400 text-amber-500" : ""}`} />
      </button>
      {it.hidden ? (
        <button type="button" onClick={() => setFlag(it, { hidden: false })}
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"><Eye className="h-4 w-4" />Geri al</button>
      ) : (
        <button type="button" onClick={() => setFlag(it, { hidden: true })} title="Beğenmedim; listeden gizle"
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"><EyeOff className="h-4 w-4" />Gizle</button>
      )}
    </>
  );

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900"><Radar className="h-5 w-5 text-indigo-600" />Ürün Keşfi</h1>
          <p className="mt-1 text-sm text-gray-500">
            Eklentinin yan panelindeki “Keşif” sekmesinden başlattığın anahtar kelime taramalarının sonuçları.
          </p>
        </div>
        <button
          onClick={onClear}
          disabled={clear.isPending || !data?.items.length}
          className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:bg-red-300"
        >
          {clear.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          Temizle
        </button>
      </div>

      <div className="mb-2 flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <label className="text-xs font-medium text-gray-700">
          <span className="mb-1 block">Kayıtlı filtre</span>
          <select value={activePreset} onChange={(e) => onPickPreset(e.target.value)} className={`${INPUT} w-56`}>
            <option value="">{presets.length ? "Seç…" : "Henüz kayıtlı filtre yok"}</option>
            {presets.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-gray-700">
          <span className="mb-1 block">Filtre adı</span>
          <input type="text" value={presetName} onChange={(e) => setPresetName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") onSavePreset(); }}
            placeholder={activePreset ? `"${activePreset}" üzerine yaz` : "ör. Yeni ve sepette"} className={`${INPUT} w-56`} />
        </label>
        <button type="button" onClick={onSavePreset} disabled={savePresets.isPending || (!presetName.trim() && !activePreset)}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          {savePresets.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Filtreyi kaydet
        </button>
        {activePreset && (
          <button type="button" onClick={onDeletePreset} className="rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50">Sil</button>
        )}
        <button type="button" onClick={() => { applyFilters(DEFAULT_FILTERS); setActivePreset(""); }}
          className="ml-auto rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100">Filtreleri sıfırla</button>
      </div>
      {savePresets.error && <div className="mb-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">{(savePresets.error as Error).message}</div>}

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <label className="text-xs font-medium text-gray-700">
          <span className="mb-1 block">Anahtar kelime</span>
          <select value={keyword} onChange={(e) => setKeyword(e.target.value)} className={INPUT}>
            <option value="">Tümü</option>
            {keywords.map((k) => <option key={k.keyword} value={k.keyword}>{k.keyword} ({k.count})</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-gray-700">
          <span className="mb-1 block">Sırala</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} className={INPUT}>
            {SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-gray-700">
          <span className="mb-1 block">Yaş en çok (gün)</span>
          <input type="number" min={0} value={maxAge} onChange={(e) => setMaxAge(e.target.value)} className={`${INPUT} w-32`} />
        </label>
        <label className="text-xs font-medium text-gray-700">
          <span className="mb-1 block">Potansiyel en az</span>
          <input type="number" min={0} max={100} value={minPotential} onChange={(e) => setMinPotential(e.target.value)} className={`${INPUT} w-32`} />
        </label>
        <label className="text-xs font-medium text-gray-700">
          <span className="mb-1 block">Negatif kelimeler (virgülle)</span>
          <input type="text" value={exclude} onChange={(e) => changeExclude(e.target.value)} placeholder="sweatshirt, hoodie, baby" className={`${INPUT} w-64`} />
        </label>
        <label className="flex items-center gap-2 py-2 text-sm text-gray-700">
          <input type="checkbox" checked={onlyDeep} onChange={(e) => setOnlyDeep(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
          Yalnız tam veri
        </label>
        <label className="flex items-center gap-2 py-2 text-sm text-gray-700">
          <input type="checkbox" checked={hideAds} onChange={(e) => setHideAds(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
          Reklamları gizle
        </label>
        <label className="flex items-center gap-2 py-2 text-sm text-gray-700">
          <input type="checkbox" checked={hideAnalyzed} onChange={(e) => setHideAnalyzed(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
          Analiz edilenleri gizle
        </label>
        <label className="flex items-center gap-2 py-2 text-sm text-gray-700">
          <input type="checkbox" checked={onlyWatched} onChange={(e) => setOnlyWatched(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
          Yalnız takip edilenler
        </label>
        <label className="flex items-center gap-2 py-2 text-sm text-gray-700">
          <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
          Gizlenenleri göster
        </label>
        <label className="flex items-center gap-2 py-2 text-sm text-gray-700" title="Telifli kelime havuzuna takılan ve AI'nin telif riski yüksek dediği ürünleri gizle">
          <input type="checkbox" checked={hideIp} onChange={(e) => setHideIp(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
          Telifli olabilecekleri gizle
        </label>
        <button type="button" onClick={() => setIpEdit(ipTerms.join(", "))}
          className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100">
          <ShieldAlert className="h-4 w-4" /> Telifli kelimeler ({ipTerms.length})
        </button>
        <div className="ml-auto flex rounded-lg border border-gray-200 p-0.5" role="group" aria-label="Görünüm">
          {([["grid", LayoutGrid, "Izgara"], ["table", List, "Tablo"]] as const).map(([v, Icon, label]) => (
            <button key={v} type="button" onClick={() => changeView(v)} aria-pressed={view === v} title={label} aria-label={label}
              className={`rounded-md px-2.5 py-1.5 ${view === v ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-50"}`}>
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>
      </div>

      {!!data?.items.length && (
        <>
          <KeywordSummary items={data.items} onPick={(k) => { setKeyword(k); setGroup(null); }} />
          <TopWords rows={filtered} />
        </>
      )}
      {group && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-indigo-50 p-3 text-sm text-indigo-700">
          Benzer ilan grubu gösteriliyor ({rows.length})
          <button type="button" onClick={() => setGroup(null)} className="ml-auto rounded-lg px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100">Gruptan çık</button>
        </div>
      )}
      {patch.error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{(patch.error as Error).message}</div>}

      {rows.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setSelected(new Set(rows.filter((r) => !analyzedId(r)).map((r) => r.id)))}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100">Analiz edilmemişlerin tümünü seç ({rows.filter((r) => !analyzedId(r)).length})</button>
          {selected.size > 0 && (
            <button type="button" onClick={() => setSelected(new Set())} className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100">Seçimi temizle</button>
          )}
          <button type="button" disabled={aiEval.isPending}
            onClick={() => aiEval.mutate(selected.size ? [...selected] : rows.filter((r) => !r.aiEval).slice(0, 20).map((r) => r.id))}
            title="Görsel ve satış verileriyle potansiyeli ve telif riskini değerlendirir (seçim yoksa listedeki ilk 20 değerlendirilmemiş ürün)"
            className="ml-auto inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {aiEval.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-indigo-600" />}
            {aiEval.isPending ? "Değerlendiriliyor…" : `AI ile değerlendir (${selected.size || Math.min(20, rows.filter((r) => !r.aiEval).length)})`}
          </button>
          <button type="button" onClick={() => sendToAnalysis(true)} disabled={!selected.size || sending}
            title="VDS'teki (görev alan) eklenti analiz etsin"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Görev olarak gönder (VDS)
          </button>
          <button type="button" onClick={() => sendToAnalysis()} disabled={!selected.size || sending}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Seçilenleri analize gönder{selected.size ? ` (${selected.size})` : ""}
          </button>
        </div>
      )}
      {aiMsg && (
        <div className={`mb-4 flex items-start justify-between gap-2 rounded-lg p-3 text-sm ${aiMsg.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
          <span>{aiMsg.text}</span>
          <button type="button" onClick={() => setAiMsg(null)} aria-label="Kapat" title="Kapat" className="shrink-0 rounded-lg p-0.5 hover:bg-black/5"><X className="h-4 w-4" /></button>
        </div>
      )}
      {sendMsg && (
        <div className={`mb-4 rounded-lg p-3 text-sm ${sendMsg.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{sendMsg.text}</div>
      )}

      {clear.error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{(clear.error as Error).message}</div>}

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />Yükleniyor…
        </div>
      ) : error ? (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{(error as Error).message}</div>
      ) : !data?.items.length ? (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center shadow-sm">
          <Radar className="mx-auto h-8 w-8 text-gray-300" />
          <p className="mt-3 text-sm text-gray-500">
            Henüz keşif sonucu yok. Eklentinin yan panelindeki “Keşif” sekmesinden anahtar kelime girip taramayı başlat.
          </p>
        </div>
      ) : view === "grid" ? (
        <>
          <p className="mb-2 text-xs text-gray-500">Seçmek için fotoğrafa tıkla; Shift ile tıklayınca aradakilerin hepsi seçilir. Etsy'de açmak için başlığa tıkla.</p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {rows.map((it, i) => (
              <div key={it.id}
                className={`group overflow-hidden rounded-xl border bg-white shadow-sm transition-shadow hover:shadow-md ${it.hidden ? "opacity-60" : ""} ${selected.has(it.id) ? "border-indigo-400 ring-2 ring-indigo-200" : "border-gray-200"}`}
                style={it.ruleColor ? { outline: `3px solid ${it.ruleColor}`, outlineOffset: 2 } : undefined}>
                <div className="relative aspect-square bg-gray-100">
                  {analyzedId(it) ? (
                    <a href={it.url} target="_blank" rel="noreferrer" tabIndex={-1} aria-hidden="true" className="block h-full w-full">
                      {it.imageUrl && <img src={bigImage(it.imageUrl)} alt="" loading="lazy" className="h-full w-full object-cover" />}
                    </a>
                  ) : (
                    // Fotografin tamami secim dugmesi; Shift ile aralik secimi
                    <button type="button" onClick={(e) => pick(i, e.shiftKey)} aria-pressed={selected.has(it.id)}
                      aria-label={`Analiz için seç: ${it.title}`} className="block h-full w-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-300">
                      {it.imageUrl && <img src={bigImage(it.imageUrl)} alt="" loading="lazy" className="h-full w-full object-cover" />}
                      <span className={`absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-lg border-2 shadow-sm ${selected.has(it.id) ? "border-indigo-600 bg-indigo-600 text-white" : "border-white bg-white/80 text-transparent group-hover:border-indigo-300"}`}>
                        <Check className="h-4 w-4" />
                      </span>
                      {selected.has(it.id) && (
                        <span className="absolute inset-0 flex items-center justify-center bg-indigo-600/25">
                          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg"><Check className="h-8 w-8" /></span>
                        </span>
                      )}
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1 border-b border-gray-100 px-3 py-2">
                  {it.potential != null && (
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${potentialCls(it.potential)}`}>Potansiyel {it.potential}</span>
                  )}
                  {it.ruleName && (
                    <span className="rounded-full px-2 py-0.5 text-xs font-medium text-white" style={{ background: it.ruleColor ?? undefined }}>{it.ruleName}</span>
                  )}
                  {it.isAd && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">Reklam</span>}
                  {ipHit(it.title) && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600" title="Telifli kelime havuzunda">
                      <ShieldAlert className="h-3 w-3" />{ipHit(it.title)}
                    </span>
                  )}
                  {analyzedId(it) && (
                    <a href={`/analyses/${analyzedId(it)}`} className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100">
                      Analiz edildi →
                    </a>
                  )}
                  {!group && groups.get(it.id) && (
                    <button type="button" onClick={() => setGroup(groups.get(it.id)!)} title="Yalnız bu grubu göster"
                      className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-200">{groups.get(it.id)!.length} benzer ilan</button>
                  )}
                  <span className="ml-auto flex items-center gap-0.5">{actions(it)}</span>
                </div>
                <div className="space-y-1.5 p-3">
                  <a href={it.url} target="_blank" rel="noreferrer" className="line-clamp-2 text-sm text-gray-900 hover:text-indigo-700">{it.title}</a>
                  <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
                    <span className="truncate">{it.shopName ?? ""}</span>
                    {it.price && <span className="shrink-0 font-semibold text-gray-900">{it.price}</span>}
                  </div>
                  {shopInfo(it) && <div className="text-xs text-gray-500">Mağaza: {shopInfo(it)}</div>}
                  <div className="text-xs text-gray-600">
                    <span className={toneCls(it.ageDays, ...AGE, true)} title="Liste yaşı">{fmt(it.ageDays)} gün</span>
                    {" · "}{fmt(it.views)} görüntülenme{it.viewsPerDay != null && <> (<span className={toneCls(it.viewsPerDay, ...VPD)}>{fmt(it.viewsPerDay, 1)}/gün</span>)</>}
                    {" · "}{fmt(it.favorites)} favori{it.favPerDay != null && <> (<span className={toneCls(it.favPerDay, ...FPD)}>{fmt(it.favPerDay, 1)}/gün</span>)</>}
                  </div>
                  {it.aiEval && (
                    <div className="rounded-lg border border-gray-100 bg-gray-50 p-2 text-xs text-gray-600">
                      <div className="mb-1 flex flex-wrap items-center gap-1">
                        <span className="inline-flex items-center gap-1 font-semibold text-gray-900"><Sparkles className="h-3 w-3 text-indigo-600" />AI {it.aiEval.score}</span>
                        <span className={`rounded-full px-2 py-0.5 font-medium ${VERDICT[it.aiEval.verdict][1]}`}>{VERDICT[it.aiEval.verdict][0]}</span>
                        <span className={`rounded-full px-2 py-0.5 font-medium ${CP_LEVEL[it.aiEval.copyright.level][1]}`}
                          title={[it.aiEval.copyright.note, it.aiEval.copyright.terms.join(", ")].filter(Boolean).join(" — ")}>
                          {CP_LEVEL[it.aiEval.copyright.level][0]}{it.aiEval.copyright.terms.length ? `: ${it.aiEval.copyright.terms.join(", ")}` : ""}
                        </span>
                      </div>
                      <p className="line-clamp-3" title={it.aiEval.why}>{it.aiEval.why}</p>
                      {it.aiEval.idea && <p className="mt-1 line-clamp-2 text-gray-500" title={it.aiEval.idea}>Fikir: {it.aiEval.idea}</p>}
                    </div>
                  )}
                  {growth(it) && <div className="text-xs text-gray-500">Son taramadan beri: <GrowthText it={it} /></div>}
                  {it.deep ? (
                    <div className="flex flex-wrap gap-1">
                      {it.views24 != null && <span className={`rounded-full px-2 py-0.5 text-xs ${pillCls(it.views24, ...V24)}`}>24 saatte {it.views24} görüntülenme</span>}
                      {it.inCarts != null && <span className={`rounded-full px-2 py-0.5 text-xs ${pillCls(it.inCarts, ...CARTS)}`}>{it.inCarts} sepette</span>}
                      {it.recentPurchases != null && <span className={`rounded-full px-2 py-0.5 text-xs ${pillCls(it.recentPurchases, ...SALES)}`}>24 saatte {it.recentPurchases} satış</span>}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500" title={NOT_DEEP}>Sepet / 24 saat verisi yok</div>
                  )}
                  <div className="text-xs text-gray-500">{it.keyword} · s.{it.page} #{it.rank}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-xs text-gray-500">{rows.length} ürün</div>
        </>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2"><span className="sr-only">Seç</span></th>
                <th className="px-3 py-2" />
                <th className="px-3 py-2">Ürün</th>
                <th className="px-3 py-2">Anahtar kelime</th>
                <th className="px-3 py-2 text-right">Yaş (gün)</th>
                <th className="px-3 py-2 text-right">Görüntülenme</th>
                <th className="px-3 py-2 text-right">Favori</th>
                <th className="px-3 py-2 text-right" title="Son taramadan beri günlük artış">Artış/gün</th>
                <th className="px-3 py-2 text-right">24 saat görüntülenme</th>
                <th className="px-3 py-2 text-right">Sepet</th>
                <th className="px-3 py-2 text-right">24s satış</th>
                <th className="px-3 py-2 text-right">Potansiyel</th>
                <th className="px-3 py-2">AI</th>
                <th className="px-3 py-2"><span className="sr-only">İşlemler</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((it) => (
                <tr key={it.id} className="border-t border-gray-100 align-top hover:bg-gray-50">
                  <td className="px-3 py-2">
                    {analyzedId(it) ? (
                      <a href={`/analyses/${analyzedId(it)}`} className="whitespace-nowrap rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100">Analiz edildi</a>
                    ) : (
                      <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggle(it.id)} aria-label="Analiz için seç"
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {it.imageUrl
                      ? <img src={it.imageUrl} alt="" loading="lazy" className="h-12 w-12 rounded-lg object-cover" />
                      : <div className="h-12 w-12 rounded-lg bg-gray-100" />}
                  </td>
                  <td className="max-w-md px-3 py-2">
                    <a href={it.url} target="_blank" rel="noreferrer" className="line-clamp-2 font-medium text-gray-900 hover:text-indigo-700">{it.title}</a>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                      {it.shopName && <span>{it.shopName}</span>}
                      {it.price && <span>· {it.price}</span>}
                      {it.isAd && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">Reklam</span>}
                      {it.ruleName && (
                        <span className="rounded-full px-2 py-0.5 text-xs font-medium text-white" style={{ background: it.ruleColor ?? undefined }}>
                          {it.ruleName}
                        </span>
                      )}
                      {!group && groups.get(it.id) && (
                        <button type="button" onClick={() => setGroup(groups.get(it.id)!)} title="Yalnız bu grubu göster"
                          className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-200">{groups.get(it.id)!.length} benzer ilan</button>
                      )}
                    </div>
                    {shopInfo(it) && <div className="mt-0.5 text-xs text-gray-500">Mağaza: {shopInfo(it)}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-gray-700">{it.keyword}</div>
                    <div className="text-xs text-gray-500">s.{it.page} #{it.rank}</div>
                  </td>
                  <td className={`px-3 py-2 text-right ${toneCls(it.ageDays, ...AGE, true)}`}>{fmt(it.ageDays)}</td>
                  <td className="px-3 py-2 text-right">
                    {fmt(it.views)}
                    {it.viewsPerDay != null && <div className={`text-xs ${toneCls(it.viewsPerDay, ...VPD)}`}>{fmt(it.viewsPerDay, 1)}/gün</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {fmt(it.favorites)}
                    {it.favPerDay != null && <div className={`text-xs ${toneCls(it.favPerDay, ...FPD)}`}>{fmt(it.favPerDay, 1)}/gün</div>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-xs text-gray-500">
                    {growth(it) ? <GrowthText it={it} stacked /> : "—"}
                  </td>
                  {([[it.views24, V24], [it.inCarts, CARTS], [it.recentPurchases, SALES]] as const).map(([v, th], i) => (
                    <td key={i} className={`px-3 py-2 text-right ${it.deep ? toneCls(v, th[0], th[1]) : ""}`} title={it.deep ? undefined : NOT_DEEP}>
                      {it.deep ? fmt(v) : "—"}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    {it.potential == null ? "—" : (
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${potentialCls(it.potential)}`}>{it.potential}</span>
                    )}
                  </td>
                  <td className="max-w-[220px] px-3 py-2 text-xs">
                    {it.aiEval ? (
                      <div title={[it.aiEval.why, it.aiEval.copyright.note, it.aiEval.idea && `Fikir: ${it.aiEval.idea}`].filter(Boolean).join("\n")}>
                        <div className="flex flex-wrap items-center gap-1">
                          <span className="font-semibold text-gray-900">{it.aiEval.score}</span>
                          <span className={`rounded-full px-2 py-0.5 font-medium ${VERDICT[it.aiEval.verdict][1]}`}>{VERDICT[it.aiEval.verdict][0]}</span>
                          <span className={`rounded-full px-2 py-0.5 font-medium ${CP_LEVEL[it.aiEval.copyright.level][1]}`}>{CP_LEVEL[it.aiEval.copyright.level][0]}</span>
                        </div>
                        <p className="mt-1 line-clamp-2 text-gray-500">{it.aiEval.why}</p>
                      </div>
                    ) : "—"}
                    {ipHit(it.title) && <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 font-medium text-red-600"><ShieldAlert className="h-3 w-3" />{ipHit(it.title)}</div>}
                  </td>
                  <td className="px-3 py-2"><div className="flex items-center justify-end gap-0.5">{actions(it)}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500">{rows.length} ürün</div>
        </div>
      )}

      {ipEdit != null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setIpEdit(null)}
          onKeyDown={(e) => { if (e.key === "Escape") setIpEdit(null); }}>
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900"><ShieldAlert className="h-4 w-4 text-indigo-600" />Telifli kelime havuzu</h2>
              <button type="button" onClick={() => setIpEdit(null)} aria-label="Kapat" title="Kapat" className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5">
              <p className="mb-2 text-sm text-gray-500">Başlığında bu kelimelerden biri tam kelime olarak geçen ürünler işaretlenir; "Telifli olabilecekleri gizle" açıksa listeden çıkar. Virgülle ayır.</p>
              <textarea value={ipEdit} onChange={(e) => setIpEdit(e.target.value)} rows={10} autoFocus
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
              {saveIp.error && <div className="mt-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">{(saveIp.error as Error).message}</div>}
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-5 py-3">
              <button type="button" onClick={() => setIpEdit(DEFAULT_IP_TERMS.join(", "))} className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100">Varsayılana dön</button>
              <div className="flex gap-2">
                <button type="button" onClick={() => setIpEdit(null)} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">İptal</button>
                <button type="button" disabled={saveIp.isPending}
                  onClick={() => saveIp.mutate([...new Set(ipEdit.split(",").map((x) => x.trim().toLowerCase()).filter(Boolean))])}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
                  {saveIp.isPending && <Loader2 className="h-4 w-4 animate-spin" />}Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
